import * as React from 'react';
import { useSelector } from 'react-redux';

import { trackMortgagePaymentClick, trackMortgagePaymentInfoLinkClick, trackMortgagePaymentInfoShow } from './tracking';
import { getMortgageType } from './utils';
import { DailyrentPrice } from '../../../../shared/components/DailyrentPrice';
import { MortgagePaymentInformer } from '../../../components/MortgagePaymentInformer';
import { Price } from '../../../components/Price';
import { useOfferContext } from '../../../context';
import { isCommercial } from '../../../utils/computedProps/commercial/isCommercial';
import { getIsDailyRent } from '../../../utils/computedProps/price/rent/getIsDailyRent';
import { CommercialPriceContainer } from '../commercial/CommercialPriceContainer';
import { getTermsValue } from 'shared/json_query/reducers/helpers';
import { selectSource } from 'shared/selectors';
import { selectMortgageMonthlyPaymentExperiment } from 'shared/selectors/experiments/selectMortgageMonthlyPaymentExperiment';
import { useApplicationContext } from 'shared/utils/applicationContext';

interface IPriceContainerProps {
  isNewHighlighting: boolean;
}

/**
 * Формирует query-параметры для URL на основе выбранных типов ипотеки
 */
export function buildMortgageQueryParams(promoSearchTypes: string[]): string {
  const selectedMortgageType = getMortgageType(promoSearchTypes);

  if (!selectedMortgageType) {
    return 'open_calculator=true';
  }

  return `open_calculator=true&mortgage_type=${selectedMortgageType}`;
}

export function PriceContainer({ isNewHighlighting }: IPriceContainerProps) {
  const { offer } = useOfferContext();

  if (isCommercial(offer)) {
    return <CommercialPriceContainer />;
  }

  return <ResidentialPriceContainer isNewHighlighting={isNewHighlighting} />;
}

function ResidentialPriceContainer({ isNewHighlighting }: IPriceContainerProps) {
  const { priceInfo, price, isColored, discount, offer, jsonQuery } = useOfferContext();

  const { config } = useApplicationContext();
  const isV2BookedFromDeveloperEnabled = config.get<boolean>('Newbuilding.BookedFromDeveloperDesktop.Enabled');
  const isMortgageMonthlyPaymentEnabled = config.get<boolean>('Newbuilding.MortgageMonthlyPayment.Enabled');
  const isBookedFromDeveloperAvaliable = Boolean(offer.isBookedFromDeveloper && isV2BookedFromDeveloperEnabled);

  const isMortgageMonthlyPaymentExperiment = useSelector(selectMortgageMonthlyPaymentExperiment);
  const isMortgageMonthlyPaymentAvaliable = isMortgageMonthlyPaymentExperiment && isMortgageMonthlyPaymentEnabled;

  const { category, dailyrent } = offer;
  const { priceInfo: dailyrentPriceInfo } = dailyrent || {};
  const isDailyrent = getIsDailyRent(category);
  const showDailyrentPrice = isDailyrent && dailyrentPriceInfo;

  const source = useSelector(selectSource);

  // Получаем выбранные типы ипотеки из фильтров
  const promoSearchTypes = getTermsValue('promo_search_types')(jsonQuery);
  const tooltipLink = React.useMemo(() => {
    const queryParams = buildMortgageQueryParams(promoSearchTypes);
    /* istanbul ignore next */
    const separator = offer.fullUrl.includes('?') ? '&' : '?';

    return `${offer.fullUrl}${separator}${queryParams}`;
  }, [offer.fullUrl, promoSearchTypes]);

  const onMortgagePriceClick = React.useCallback(() => {
    trackMortgagePaymentClick({ offerId: offer.id, jsonQuery });
  }, [offer, jsonQuery]);

  const onMortgagePriceTooltipShow = React.useCallback(() => {
    trackMortgagePaymentInfoShow({ offerId: offer.id, jsonQuery });
  }, [offer, jsonQuery]);

  const onMortgagePriceTooltipLinkClick = React.useCallback(() => {
    trackMortgagePaymentInfoLinkClick({ offerId: offer.id, jsonQuery, source });
  }, [offer, jsonQuery, source]);

  if (showDailyrentPrice) {
    const { finalPricePerNight, originalPricePerNight, discountPercent } = dailyrentPriceInfo;

    return (
      <DailyrentPrice
        pricePerNight={finalPricePerNight}
        originalPricePerNight={originalPricePerNight}
        discountPercent={discountPercent}
        priceDetails={priceInfo}
      />
    );
  }

  return (
    <Price
      priceInfo={priceInfo}
      price={price}
      discount={discount}
      isHighlighted={isNewHighlighting && /* istanbul ignore next*/ isColored}
      isMuted={isBookedFromDeveloperAvaliable}
      mortgagePayment={
        isMortgageMonthlyPaymentAvaliable &&
        offer.mortgagePayment && (
          <MortgagePaymentInformer
            title={offer.mortgagePayment.title}
            programTypeWithRate={offer.mortgagePayment.programTypeWithRate}
            tooltipTitle={offer.mortgagePayment.descriptionDialog.title}
            tooltipLink={tooltipLink}
            onPriceClick={onMortgagePriceClick}
            onTooltipShow={onMortgagePriceTooltipShow}
            onTooltipLinkClick={onMortgagePriceTooltipLinkClick}
          />
        )
      }
    />
  );
}

PriceContainer.displayName = 'PriceContainer';
